import { Rng } from '../core/rng';
import { lerp, smoothstep, TAU } from '../core/math';
import type { SimEvents } from './events';

export type WeatherKind = 'clear' | 'fair' | 'overcast' | 'fog' | 'rain' | 'storm';

export const WEATHER_KINDS: readonly WeatherKind[] = ['clear', 'fair', 'overcast', 'fog', 'rain', 'storm'];

export interface WeatherParams {
  /** Fraction of sky covered by cumulus, 0..1. */
  cloudCover: number;
  /** Cloud extinction multiplier. */
  cloudDensity: number;
  /** Cloud base altitude, m. */
  cloudBase: number;
  /** Cloud layer thickness, m. */
  cloudThickness: number;
  /** Uniform stratus / overcast deck, 0..1. */
  stratus: number;
  /** Sea fog density, 0..1. */
  fog: number;
  /** Rain intensity, 0..1. */
  rain: number;
  /** Mean wind speed at 10 m, m/s. */
  windSpeed: number;
  gustiness: number;
  /** Lightning strikes per minute. */
  lightning: number;
  /** Multiplier on wind-sea height. */
  waveScale: number;
  /** Background swell significant height, m. */
  swellHeight: number;
  /** Crest sharpness target. */
  chop: number;
  /** Aerosol haze 0..1 (atmospheric perspective). */
  haze: number;
}

export const WEATHER_PRESETS: Record<WeatherKind, WeatherParams> = {
  clear: { cloudCover: 0.12, cloudDensity: 0.7, cloudBase: 1500, cloudThickness: 1000, stratus: 0, fog: 0, rain: 0, windSpeed: 5.5, gustiness: 0.15, lightning: 0, waveScale: 0.85, swellHeight: 0.9, chop: 0.62, haze: 0.12 },
  fair: { cloudCover: 0.32, cloudDensity: 0.85, cloudBase: 1250, cloudThickness: 1600, stratus: 0, fog: 0, rain: 0, windSpeed: 8.5, gustiness: 0.25, lightning: 0, waveScale: 0.85, swellHeight: 1.1, chop: 0.72, haze: 0.22 },
  overcast: { cloudCover: 0.86, cloudDensity: 1.0, cloudBase: 950, cloudThickness: 1900, stratus: 0.7, fog: 0.08, rain: 0, windSpeed: 10.5, gustiness: 0.3, lightning: 0, waveScale: 0.85, swellHeight: 1.3, chop: 0.8, haze: 0.45 },
  fog: { cloudCover: 0.55, cloudDensity: 0.8, cloudBase: 500, cloudThickness: 900, stratus: 0.65, fog: 1, rain: 0, windSpeed: 3.2, gustiness: 0.1, lightning: 0, waveScale: 0.75, swellHeight: 0.7, chop: 0.5, haze: 0.8 },
  rain: { cloudCover: 0.96, cloudDensity: 1.25, cloudBase: 700, cloudThickness: 2700, stratus: 0.9, fog: 0.28, rain: 0.7, windSpeed: 13, gustiness: 0.45, lightning: 0.4, waveScale: 0.88, swellHeight: 1.7, chop: 0.88, haze: 0.6 },
  storm: { cloudCover: 1, cloudDensity: 1.7, cloudBase: 420, cloudThickness: 4200, stratus: 1, fog: 0.32, rain: 1, windSpeed: 21, gustiness: 0.7, lightning: 5, waveScale: 0.8, swellHeight: 2.6, chop: 1.0, haze: 0.7 },
};

/** Plausible successors for dynamic weather (a small Markov chain). */
const NEXT: Record<WeatherKind, readonly WeatherKind[]> = {
  clear: ['fair', 'fair', 'overcast', 'fog'],
  fair: ['clear', 'overcast', 'overcast', 'rain', 'fog'],
  overcast: ['fair', 'rain', 'rain', 'fog', 'storm'],
  fog: ['fair', 'overcast', 'clear'],
  rain: ['overcast', 'storm', 'storm', 'fair'],
  storm: ['rain', 'rain', 'overcast'],
};

const KEYS = Object.keys(WEATHER_PRESETS.clear) as (keyof WeatherParams)[];

export interface Lightning {
  x: number;
  z: number;
  /** Seconds since the strike started. */
  age: number;
  /** Flash brightness this frame, 0..1+. */
  flash: number;
  intensity: number;
  seed: number;
  strokes: number[];
}

export class Weather {
  readonly params: WeatherParams = { ...WEATHER_PRESETS.fair };
  private from: WeatherParams = { ...WEATHER_PRESETS.fair };
  private to: WeatherParams = { ...WEATHER_PRESETS.fair };
  private blend = 1;
  private duration = 1;
  kind: WeatherKind = 'fair';
  dynamic = true;
  private hold = 0;
  /** Compass bearing the wind comes from; wanders slowly. */
  windFrom: number;
  private windWanderTarget: number;
  private readonly baseWindFrom: number;
  lightning: Lightning | null = null;
  /** Total flash brightness (sum of active strikes). */
  flash = 0;

  constructor(private readonly rng: Rng, kind: WeatherKind, windFrom: number) {
    this.windFrom = windFrom;
    this.baseWindFrom = windFrom;
    this.windWanderTarget = windFrom;
    this.set(kind, 0);
    this.hold = this.rng.range(150, 260);
  }

  /** Transition to a preset over `seconds` (0 = instantly). */
  set(kind: WeatherKind, seconds = 45): void {
    this.kind = kind;
    Object.assign(this.from, this.params);
    Object.assign(this.to, WEATHER_PRESETS[kind]);
    this.duration = Math.max(seconds, 1e-3);
    this.blend = seconds <= 0 ? 1 : 0;
    if (seconds <= 0) Object.assign(this.params, this.to);
  }

  /** 0..1 progress of the current transition. */
  get transition(): number {
    return this.blend;
  }

  update(dt: number, shipX: number, shipZ: number, events: SimEvents): void {
    if (this.blend < 1) {
      this.blend = Math.min(1, this.blend + dt / this.duration);
      const t = smoothstep(0, 1, this.blend);
      for (const k of KEYS) this.params[k] = lerp(this.from[k], this.to[k], t);
    }

    if (this.dynamic) {
      this.hold -= dt;
      if (this.hold <= 0 && this.blend >= 1) {
        this.set(this.rng.pick(NEXT[this.kind]), this.rng.range(50, 90));
        this.hold = this.rng.range(160, 320);
        this.windWanderTarget = this.baseWindFrom + this.rng.range(-0.6, 0.6);
      }
    }
    // Slow veer/back of the wind toward the wander target.
    let d = (this.windWanderTarget - this.windFrom) % TAU;
    if (d > Math.PI) d -= TAU;
    if (d < -Math.PI) d += TAU;
    this.windFrom += d * (1 - Math.exp(-dt * 0.01));

    this.updateLightning(dt, shipX, shipZ, events);
  }

  private updateLightning(dt: number, shipX: number, shipZ: number, events: SimEvents): void {
    const rate = this.params.lightning / 60;
    if (rate > 0 && !this.lightning && this.rng.chance(rate * dt)) {
      const near = this.rng.chance(0.18 * this.params.rain);
      const dist = near ? this.rng.range(500, 1500) : this.rng.range(1800, 11000);
      const a = this.rng.range(0, TAU);
      const strokes: number[] = [0];
      const n = 1 + this.rng.int(4);
      let t = 0;
      for (let i = 1; i < n; i++) {
        t += this.rng.range(0.05, 0.22);
        strokes.push(t);
      }
      this.lightning = {
        x: shipX + Math.cos(a) * dist,
        z: shipZ + Math.sin(a) * dist,
        age: 0,
        flash: 0,
        intensity: this.rng.range(0.6, 1) * (near ? 1.4 : 1),
        seed: this.rng.int(1 << 30),
        strokes,
      };
      events.push({ type: 'lightning', x: this.lightning.x, z: this.lightning.z, distance: dist, intensity: this.lightning.intensity });
    }
    this.flash = 0;
    const l = this.lightning;
    if (l) {
      l.age += dt;
      let f = 0;
      for (const s of l.strokes) {
        const a = l.age - s;
        if (a >= 0) f = Math.max(f, Math.exp(-a * 14) * (a < 0.03 ? 1 : 0.8));
      }
      l.flash = f * l.intensity;
      this.flash = l.flash;
      if (l.age > l.strokes[l.strokes.length - 1]! + 0.6) this.lightning = null;
    }
  }
}
